import { useState, useEffect, useRef, useCallback } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';

type UseDebouncedFieldProps<T> = {
    initialValue: T;
    onChange: (value: T) => void;
    delay?: number;
    isEqual?: (a: T, b: T) => boolean;
};

export const useDebouncedField = <T>({
    initialValue,
    onChange,
    delay = 300,
    isEqual = (a, b) => a === b,
}: UseDebouncedFieldProps<T>) => {
    const [value, setValue] = useState<T>(initialValue);
    const lastCommittedRef = useRef<T>(initialValue);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    const debouncedCommit = useDebouncedCallback((newValue: T) => {
        if (!isEqual(newValue, lastCommittedRef.current)) {
            lastCommittedRef.current = newValue;
            onChangeRef.current(newValue);
        }
    }, delay);

    useEffect(() => {
        setValue(initialValue);
        lastCommittedRef.current = initialValue;
        debouncedCommit.cancel();
    }, [initialValue, debouncedCommit]);

    useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

    const handleChange = useCallback(
        (newValue: T) => {
            setValue(newValue);
            debouncedCommit(newValue);
        },
        [debouncedCommit],
    );

    return { value, onChange: handleChange, cancel: debouncedCommit.cancel };
};
